'use client';

import * as React from 'react';
import { flushSync } from 'react-dom';
import { useTheme } from 'next-themes';
import { AnimatePresence, motion } from 'framer-motion';
import { Moon, Sun } from 'lucide-react';
import { cn } from '@/lib/utils';
import { SNAPPY } from '@/lib/motion';
import { usePrefersReducedMotion } from '@/shared/hooks/use-prefers-reduced-motion';

type DocWithVT = Document & {
  startViewTransition?: (cb: () => void) => { finished: Promise<void> };
};

/**
 * Light/dark switch. Toggles the resolved theme (flipping the `.dark` class on
 * <html> that the whole token system keys off) and morphs between the two
 * palettes with the View Transitions API: an expanding circle clipped to the
 * button's exact position, so the surfaces visually swap in place instead of
 * hard-cutting (globals.css owns the `::view-transition` keyframe).
 *
 * The sun/moon glyph itself cross-rotates on every toggle. Everything degrades
 * gracefully — no View Transitions support or reduced-motion → an instant,
 * flash-free swap. Mounted-guarded so SSR/CSR markup always matches.
 */
export function ThemeToggle({ className }: { className?: string }) {
  const { resolvedTheme, setTheme } = useTheme();
  const reduced = usePrefersReducedMotion();
  const [mounted, setMounted] = React.useState(false);
  React.useEffect(() => setMounted(true), []);

  const isDark = mounted && resolvedTheme === 'dark';

  const toggle = React.useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      const next = resolvedTheme === 'dark' ? 'light' : 'dark';
      const root = document.documentElement;
      const doc = document as DocWithVT;

      const commit = () => {
        // Apply the class synchronously so the "new" snapshot is the new theme,
        // then let next-themes persist + keep its own state in sync.
        root.classList.toggle('dark', next === 'dark');
        setTheme(next);
      };

      if (reduced || typeof doc.startViewTransition !== 'function') {
        // No morph here — but still suppress every element's colour transition
        // (globals.css keys off this attribute) so the swap is an instant, clean
        // cut instead of a laggy whole-page colour fade. Re-enable after paint.
        root.setAttribute('data-theme-switching', '');
        commit();
        requestAnimationFrame(() =>
          requestAnimationFrame(() => root.removeAttribute('data-theme-switching')),
        );
        return;
      }

      // Origin = the button's centre; radius = the farthest viewport corner,
      // nudged out ~6% so the circle fully clears the corners a hair before the
      // curve settles — that hides the final, slowest pixels of travel and the
      // reveal reads as finishing cleanly instead of easing to a visible stop.
      const rect = e.currentTarget.getBoundingClientRect();
      const x = rect.left + rect.width / 2;
      const y = rect.top + rect.height / 2;
      const r =
        Math.hypot(Math.max(x, window.innerWidth - x), Math.max(y, window.innerHeight - y)) * 1.06;
      root.style.setProperty('--vt-x', `${x}px`);
      root.style.setProperty('--vt-y', `${y}px`);
      root.style.setProperty('--vt-r', `${r}px`);
      root.setAttribute('data-theme-morph', 'true');

      const transition = doc.startViewTransition(() => flushSync(commit));
      transition.finished.finally(() => root.removeAttribute('data-theme-morph'));
    },
    [reduced, resolvedTheme, setTheme],
  );

  return (
    <button
      type="button"
      onClick={toggle}
      aria-label={mounted ? `Switch to ${isDark ? 'light' : 'dark'} theme` : 'Toggle theme'}
      className={cn(
        'inline-flex h-10 w-10 items-center justify-center rounded-full border border-[var(--border)]',
        'bg-[var(--surface)] text-[var(--text-secondary)] shadow-[var(--shadow-sm)]',
        'transition-[color,border-color,transform] duration-200',
        'hover:border-[color-mix(in_srgb,var(--primary)_35%,transparent)] hover:text-[var(--primary)] hover:scale-105',
        'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--focus-ring)]',
        'active:scale-95 motion-reduce:hover:scale-100 motion-reduce:active:scale-100',
        className,
      )}
    >
      <span className="relative flex h-5 w-5 items-center justify-center">
        <AnimatePresence initial={false} mode="wait">
          <motion.span
            key={isDark ? 'sun' : 'moon'}
            initial={reduced ? false : { rotate: -90, scale: 0, opacity: 0 }}
            animate={{ rotate: 0, scale: 1, opacity: 1 }}
            exit={reduced ? undefined : { rotate: 90, scale: 0, opacity: 0 }}
            transition={SNAPPY}
            className="absolute inset-0 flex items-center justify-center"
          >
            {isDark ? (
              <Sun className="h-5 w-5" aria-hidden="true" />
            ) : (
              <Moon className="h-5 w-5" aria-hidden="true" />
            )}
          </motion.span>
        </AnimatePresence>
      </span>
    </button>
  );
}
